import type { InvoiceDto, PlanDto, StorageDto, SubscriptionDto } from "@bafe/api-client";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import {
	Button,
	Card,
	DataTable,
	EmptyState,
	InlineNotice,
	PageHeader,
	Section,
	SkeletonRows,
	StatusPill,
	cn,
	useToast,
	type Column,
} from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { AccountStorageSummary } from "../components/AccountStorageSummary";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorPanel } from "../components/ErrorPanel";
import { QueryState } from "../components/QueryState";
import { api } from "../lib/api";
import { formatDate, formatMoney } from "../lib/format";
import { queryKeys } from "../lib/query";
import { INVOICE_STATUS, SUBSCRIPTION_STATUS } from "../lib/status";

export function Billing() {
	const billing = useQuery({ queryKey: queryKeys.billing, queryFn: () => api.getBilling() });
	const [searchParams, setSearchParams] = useSearchParams();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	useEffect(() => {
		const legacySession = searchParams.get("fake_checkout");
		if (legacySession) {
			window.location.replace(
				`/api/v1/billing/fake-checkout/complete?session=${encodeURIComponent(legacySession)}`,
			);
			return;
		}
		if (searchParams.get("checkout") === "success") {
			void queryClient.invalidateQueries({ queryKey: queryKeys.billing });
			void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
			toast({ tone: "success", message: "Your storage plan is active." });
			searchParams.delete("checkout");
			setSearchParams(searchParams, { replace: true });
		}
	}, [queryClient, searchParams, setSearchParams, toast]);

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				title="Billing"
				description="One storage plan covers every domain and every email account on this account."
			/>

			<QueryState query={billing} errorTitle="We could not load your billing details.">
				{(data) => (
					<>
						<AccountState subscription={data.subscription} />
						<PlanSummary subscription={data.subscription} storage={data.storage} />
						<PaymentMethod />
						<PlanChooser subscription={data.subscription} />
						<Invoices invoices={data.invoices} />
					</>
				)}
			</QueryState>
		</div>
	);
}

function AccountState({ subscription }: { subscription: SubscriptionDto | null }) {
	if (!subscription) return null;

	const state = SUBSCRIPTION_STATUS[subscription.status];
	const tone =
		state.tone === "positive"
			? "positive"
			: state.tone === "critical"
				? "critical"
				: state.tone === "caution"
					? "caution"
					: "info";

	if (subscription.status === "active") return null;

	return (
		<InlineNotice tone={tone} title={state.label}>
			{state.explanation}
		</InlineNotice>
	);
}

function PlanSummary({
	subscription,
	storage,
}: {
	subscription: SubscriptionDto | null;
	storage: StorageDto;
}) {
	return (
		<Section title="Your plan">
			<div className="grid gap-4 lg:grid-cols-2">
				<Card className="flex flex-col gap-4">
					{subscription ? (
						<>
							<div className="flex flex-wrap items-start justify-between gap-2">
								<div className="flex flex-col gap-0.5">
									<p className="text-lg font-semibold text-(--text-primary)">
										{subscription.plan.name}
									</p>
									<p className="text-sm text-(--text-secondary)">
										{subscription.plan.storageLabel} total storage
									</p>
								</div>
								<StatusPill tone={SUBSCRIPTION_STATUS[subscription.status].tone}>
									{SUBSCRIPTION_STATUS[subscription.status].label}
								</StatusPill>
							</div>

							<dl className="flex flex-col gap-2">
								<Row
									label="Renews"
									value={
										subscription.cancelsAt
											? `Ends ${formatDate(subscription.cancelsAt)}`
											: formatDate(subscription.renewsAt)
									}
								/>
								<Row
									label="Amount"
									value={`${formatMoney(subscription.plan.renewalPriceMinor, subscription.plan.currency)} ${
										subscription.plan.billingPeriod === "annual" ? "per year" : "per month"
									}`}
								/>
								{subscription.trialEndsAt && (
									<Row label="Trial ends" value={formatDate(subscription.trialEndsAt)} />
								)}
							</dl>
						</>
					) : (
						<div className="flex flex-col gap-2">
							<p className="text-lg font-semibold text-(--text-primary)">No plan yet</p>
							<p className="text-sm text-(--text-secondary)">
								Choose a storage plan below to start sending and receiving.
							</p>
						</div>
					)}
				</Card>

				<Card className="flex flex-col gap-4">
					<AccountStorageSummary storage={storage} showCounts={false} />

					<dl className="flex flex-col gap-2 border-t border-(--border-subtle) pt-3">
						<Row label="Domains" value={String(storage.domainCount)} />
						<Row label="Email accounts" value={String(storage.mailboxCount)} />
					</dl>

					<p className="text-xs text-(--text-muted)">
						Domains and email accounts are shown for information. Your bill is for storage only.
					</p>
				</Card>
			</div>

			{subscription && <SubscriptionActions subscription={subscription} />}
		</Section>
	);
}

function SubscriptionActions({ subscription }: { subscription: SubscriptionDto }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [confirmCancel, setConfirmCancel] = useState(false);

	const cancelRenewal = useMutation({
		mutationFn: () => api.cancelRenewal(),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.billing });
			setConfirmCancel(false);
			toast({ tone: "success", message: "Your plan will not renew. Your mail stays until the paid period ends." });
		},
	});

	const reactivate = useMutation({
		mutationFn: () => api.reactivate(),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.billing });
			toast({ tone: "success", message: "Your plan is active again." });
		},
	});

	const cancelled = subscription.cancelsAt !== null || subscription.status === "cancelled";

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap gap-2">
				{cancelled ? (
					<Button
						variant="primary"
						pending={reactivate.isPending}
						pendingLabel="Reactivating…"
						onClick={() => reactivate.mutate()}
					>
						Reactivate plan
					</Button>
				) : (
					<Button variant="ghost" onClick={() => setConfirmCancel(true)}>
						Cancel renewal
					</Button>
				)}
			</div>

			{reactivate.isError && <ErrorPanel error={reactivate.error} title="We could not reactivate your plan." />}

			<ConfirmDialog
				open={confirmCancel}
				onClose={() => setConfirmCancel(false)}
				title="Stop your plan renewing?"
				description="Your email keeps working until the end of the period you have already paid for. Nothing is deleted on that date."
				confirmLabel="Stop renewal"
				danger
				pending={cancelRenewal.isPending}
				error={cancelRenewal.error}
				onConfirm={() => cancelRenewal.mutate()}
			/>
		</div>
	);
}

function PaymentMethod() {
	const { toast } = useToast();

	const portal = useMutation({
		mutationFn: () => api.createBillingPortal(),
		onSuccess: (result) => {
			window.location.assign(result.url);
		},
		onError: () => {
			toast({ tone: "error", message: "We could not open the billing portal. Try again in a moment." });
		},
	});

	return (
		<Section
			title="Payment method"
			description="Card details are held by our payment provider, never by Key."
		>
			<Card className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-sm text-(--text-secondary)">
					Update your card, billing address and tax details in the secure portal.
				</p>
				<Button
					variant="secondary"
					pending={portal.isPending}
					pendingLabel="Opening…"
					onClick={() => portal.mutate()}
					iconRight={<ArrowSquareOutIcon className="size-4" aria-hidden="true" />}
				>
					Manage payment method
				</Button>
			</Card>
		</Section>
	);
}

function PlanChooser({ subscription }: { subscription: SubscriptionDto | null }) {
	const plans = useQuery({ queryKey: queryKeys.plans, queryFn: () => api.listPlans() });
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const [selected, setSelected] = useState<PlanDto | null>(null);

	const change = useMutation({
		mutationFn: async (plan: PlanDto) => {
			if (subscription) return api.changeStoragePlan(plan.code);
			const checkout = await api.createCheckout({ planCode: plan.code });
			window.location.assign(checkout.url);
			return null;
		},
		onSuccess: (result) => {
			if (result === null) return;
			void queryClient.invalidateQueries({ queryKey: queryKeys.billing });
			void queryClient.invalidateQueries({ queryKey: queryKeys.storage });
			setSelected(null);
			toast({ tone: "success", message: "Your storage plan is updated." });
		},
	});

	return (
		<Section
			title={subscription ? "Increase storage or change plan" : "Choose a storage plan"}
			description="Every plan includes as many domains and email accounts as you need within fair use. You are buying storage."
		>
			<QueryState query={plans} loading={<SkeletonRows rows={3} />} errorTitle="We could not load the plans.">
				{(list) => (
					<ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{list.map((plan) => {
							const current = subscription?.plan.code === plan.code;
							return (
								<li key={plan.id}>
									<Card
										className={cn(
											"flex h-full flex-col gap-3",
											current && "border-(--color-brand-500)",
										)}
									>
										<div className="flex flex-wrap items-start justify-between gap-2">
											<div className="flex flex-col gap-0.5">
												<p className="text-base font-semibold text-(--text-primary)">
													{plan.name}
												</p>
												<p className="text-sm text-(--text-secondary)">
													{plan.storageLabel} of storage
												</p>
											</div>
											{current && <StatusPill tone="info">Current plan</StatusPill>}
											{!current && plan.badge && (
												<StatusPill tone="info">{plan.badge}</StatusPill>
											)}
										</div>

										<p className="text-lg font-semibold text-(--text-primary)">
											{formatMoney(plan.priceMinor, plan.currency)}
											<span className="text-sm font-normal text-(--text-secondary)">
												{plan.billingPeriod === "annual" ? " per year" : " per month"}
											</span>
										</p>

										<p className="text-sm text-(--text-secondary)">{plan.description}</p>

										{plan.features.length > 0 && (
											<ul className="flex flex-col gap-1 text-sm text-(--text-secondary)">
												{plan.features.map((feature) => (
													<li key={feature}>· {feature}</li>
												))}
											</ul>
										)}

										<div className="mt-auto pt-2">
											<Button
												variant={current ? "secondary" : "primary"}
												fullWidth
												disabled={current}
												onClick={() => setSelected(plan)}
											>
												{current ? "Your plan" : subscription ? "Switch to this plan" : "Choose plan"}
											</Button>
										</div>
									</Card>
								</li>
							);
						})}
					</ul>
				)}
			</QueryState>

			<ConfirmDialog
				open={selected !== null}
				onClose={() => {
					setSelected(null);
					change.reset();
				}}
				title={selected ? `Move to ${selected.name}?` : "Change plan"}
				description={
					selected
						? `Your account will have ${selected.storageLabel} of storage shared across every domain and email account. Nothing is deleted when you change plan.`
						: undefined
				}
				confirmLabel={subscription ? "Change plan" : "Activate plan"}
				pending={change.isPending}
				error={change.error}
				onConfirm={() => {
					if (selected) change.mutate(selected);
				}}
			/>
		</Section>
	);
}

const invoiceColumns: Column<InvoiceDto>[] = [
	{
		id: "number",
		header: "Invoice",
		primary: true,
		render: (invoice) => invoice.number,
	},
	{
		id: "issued",
		header: "Issued",
		render: (invoice) => formatDate(invoice.issuedAt),
	},
	{
		id: "amount",
		header: "Amount",
		render: (invoice) => formatMoney(invoice.amountMinor, invoice.currency),
	},
	{
		id: "status",
		header: "Status",
		render: (invoice) => (
			<StatusPill tone={INVOICE_STATUS[invoice.status].tone}>
				{INVOICE_STATUS[invoice.status].label}
			</StatusPill>
		),
	},
	{
		id: "link",
		header: "Document",
		align: "right",
		render: (invoice) =>
			invoice.invoiceUrl ? (
				<a
					href={invoice.invoiceUrl}
					target="_blank"
					rel="noreferrer"
					className="text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
				>
					View<span className="sr-only"> invoice {invoice.number}</span>
				</a>
			) : (
				<span className="text-sm text-(--text-muted)">—</span>
			),
	},
];

function Invoices({ invoices }: { invoices: InvoiceDto[] }) {
	return (
		<Section title="Invoices">
			<Card flush>
				<DataTable
					caption="Your invoices"
					columns={invoiceColumns}
					rows={invoices}
					rowKey={(invoice) => invoice.id}
					empty={
						<EmptyState
							title="No invoices yet"
							description="Your first invoice appears here once your plan renews."
						/>
					}
				/>
			</Card>
		</Section>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-wrap items-baseline justify-between gap-2">
			<dt className="text-sm text-(--text-secondary)">{label}</dt>
			<dd className="text-sm text-(--text-primary)">{value}</dd>
		</div>
	);
}
